/* eslint-disable roros/no-raw-html-in-routes */
// reason: <form> is a semantic HTML wrapper required for accessible form submission;
// no design-system Form component exists — Input and Button (design-system) are used for all controls.
/**
 * WorkspaceFinder — apex domain page (AC-PROD-4)
 *
 * Rendered at the apex domain (e.g. https://roros.ai) when VITE_TENANT_BASE_DOMAIN is set
 * and the current host is the base domain itself (not a subdomain).
 *
 * Behaviour:
 *   (+) Enter workspace slug → redirect to https://<slug>.<base>
 *   (−) Unknown/empty slug → inline error, no redirect
 *
 * Note: slug validity (whether the org exists) is checked client-side by
 * calling /api/register/check-slug (existing public endpoint). This avoids
 * needing a new API endpoint and reuses the existing slug-availability check.
 */

import React, { useState, FormEvent } from 'react';
import { appConfig } from '@/config/app';
import { apiFetch } from '@/lib/api';
import { t } from '@/lib/i18n';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

export function WorkspaceFinder() {
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const base = appConfig.tenantBaseDomain;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = slug.trim().toLowerCase();
    if (!trimmed) {
      setError(t('Please enter a workspace URL.'));
      return;
    }

    // Basic slug format check
    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(trimmed) && !/^[a-z0-9]$/.test(trimmed)) {
      setError(t('Invalid workspace URL. Use only lowercase letters, numbers, and hyphens.'));
      return;
    }

    setLoading(true);
    try {
      // Check slug existence: if NOT available → org exists → redirect
      const res = await apiFetch(`/api/register/check-slug?slug=${encodeURIComponent(trimmed)}`, {
        skipRefresh: true,
        skipRedirect: true,
      });
      if (!res.ok) throw new Error('Network error');
      const data = await res.json();

      if (data.data?.available === true) {
        // Slug is available → org does NOT exist
        setError(t('No workspace found for "{{slug}}". Check the URL and try again.').replace('{{slug}}', trimmed));
      } else {
        // Slug taken → org exists → navigate to tenant subdomain
        // Use current protocol so local HTTP dev testing works (production will be HTTPS)
        const proto = window.location.protocol;
        const portStr = window.location.port ? `:${window.location.port}` : '';
        window.location.href = `${proto}//${trimmed}.${base}${portStr}`;
      }
    } catch {
      setError(t('Unable to check workspace. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex flex-col items-center justify-center px-4"
      data-testid="workspace-finder"
    >
      <div className="w-full max-w-md">
        {/* Logo / branding */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{appConfig.name}</h1>
          <p className="mt-2 text-gray-600">{t('Find your workspace')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="workspace-finder-form">
            <div>
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent">
                <Input
                  id="workspace-slug"
                  type="text"
                  value={slug}
                  onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder={t('your-workspace')}
                  className="flex-1 border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  autoComplete="off"
                  autoFocus
                  disabled={loading}
                  data-testid="workspace-slug-input"
                  label={t('Workspace URL')}
                />
                <span className="px-3 py-3 text-sm text-gray-400 bg-gray-50 border-l border-gray-300 whitespace-nowrap self-end mb-0.5">
                  .{base}
                </span>
              </div>
            </div>

            {error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2"
                data-testid="workspace-finder-error"
                role="alert"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={loading || !slug.trim()}
              data-testid="workspace-finder-submit"
            >
              {loading ? t('Searching…') : t('Go to workspace →')}
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          {t("Don't have a workspace?")}{' '}
          <a href={`mailto:${appConfig.supportEmail}`} className="text-primary-600 hover:underline">
            {t('Contact us')}
          </a>
        </p>
      </div>
    </div>
  );
}
